import { ActionButton, Dropdown, type IDropdownOption } from "@fluentui/react";
import { observable, action, makeObservable } from "mobx";
import { observer } from "mobx-react";
import React from "react";
import { type Department, type VehicleGroup, ProcurementType } from "../../api";

export interface FilterVehicleListProps {
    /** This callback function allows the filter to rerender
     * the parent component. */
    setVehicleListFilter(filter: FilterVehicleListQuery): void;
}

export type FilterVehicleListQuery = {
    filteredDepartments?: string[];
    filteredVehicleGroups?: string[];
    filteredProcurementTypes?: ProcurementType[];
    isFiltered: boolean;
};

/**
 * A class which can filter the vehicle list by departments, vehicle groups
 * and vehicle procurement types.
 */
import { store } from "../../store";
import SearchableDropdown from "../atoms/searchable-dropdown";
import { defaultPageSize } from "../../utils/constants";

@observer
export class FilterVehicleList extends React.Component<FilterVehicleListProps> {
    @observable private accessor filteredDepartments: string[] = [];
    @observable private accessor filteredVehicleGroups: string[] = [];
    @observable private accessor filteredProcurementTypes: ProcurementType[] = [];
    @observable private accessor isFiltered: boolean = false;

    constructor(props: FilterVehicleListProps) {
        super(props);
        makeObservable(this);
    }

    private setDepartmentFilter(filter: string): void {
        // Remove element if it is already clicked.
        if (this.filteredDepartments.includes(filter)) {
            this.filteredDepartments = this.filteredDepartments.filter((id) => id !== filter);
            this.setVehicleListFilter();
            return;
        }
        this.isFiltered = true;
        this.filteredDepartments = [filter, ...this.filteredDepartments];
        this.setVehicleListFilter();
    }

    private setVehicleGroupFilter(filter: string): void {
        // Remove element if it is already clicked.
        if (this.filteredVehicleGroups.includes(filter)) {
            this.filteredVehicleGroups = this.filteredVehicleGroups.filter((id) => id !== filter);
            this.setVehicleListFilter();
            return;
        }
        this.isFiltered = true;
        this.filteredVehicleGroups = [filter, ...this.filteredVehicleGroups];
        this.setVehicleListFilter();
    }

    private setProcurementTypeFilter(filter: ProcurementType): void {
        // Remove element if it is already clicked.
        if (this.filteredProcurementTypes.includes(filter)) {
            this.filteredProcurementTypes = this.filteredProcurementTypes.filter(
                (id) => id !== filter,
            );
            this.setVehicleListFilter();
            return;
        }
        this.isFiltered = true;
        this.filteredProcurementTypes = [filter, ...this.filteredProcurementTypes];
        this.setVehicleListFilter();
    }

    @action.bound private clearFilter(): void {
        this.filteredDepartments = [];
        this.filteredVehicleGroups = [];
        this.filteredProcurementTypes = [];
        this.isFiltered = false;
        this.setVehicleListFilter();
    }

    private setVehicleListFilter(): void {
        const filterQuery: FilterVehicleListQuery = {
            filteredDepartments: this.filteredDepartments,
            filteredVehicleGroups: this.filteredVehicleGroups,
            filteredProcurementTypes: this.filteredProcurementTypes,
            isFiltered: this.isFiltered,
        };
        this.props.setVehicleListFilter(filterQuery);
    }

    public render(): JSX.Element {
        const procurementOptions: IDropdownOption[] = [
            {
                key: ProcurementType.PURCHASE,
                text: store.i18n.t("listVehicles.procurementType.purchase"),
            },
            {
                key: ProcurementType.LEASING,
                text: store.i18n.t("listVehicles.procurementType.leasing"),
            },
            {
                key: ProcurementType.RENTAL,
                text: store.i18n.t("listVehicles.procurementType.rental"),
            },
        ];
        return (
            <>
                <SearchableDropdown
                    hideSearch
                    query={{
                        pageSize: defaultPageSize,
                    }}
                    placeholder={store.i18n.t("schema.vehicle.department")}
                    repository={store.repositoryDepartments}
                    formatEntity={(department: Department) => department.label}
                    selectedKeys={this.filteredDepartments}
                    onChange={(departmentId: string | undefined) => {
                        if (departmentId === undefined) {
                            return;
                        }
                        this.setDepartmentFilter(departmentId);
                    }}
                    multiSelect
                />
                <SearchableDropdown
                    hideSearch
                    query={{
                        pageSize: defaultPageSize,
                    }}
                    placeholder={store.i18n.t("schema.vehicle.vehicleGroup")}
                    repository={store.repositoryVehicleGroups}
                    formatEntity={(vehicleGroup: VehicleGroup) => vehicleGroup.label}
                    selectedKeys={this.filteredVehicleGroups}
                    onChange={(vehicleGroupId: string | undefined) => {
                        if (vehicleGroupId === undefined) {
                            return;
                        }
                        this.setVehicleGroupFilter(vehicleGroupId);
                    }}
                    multiSelect
                />
                <Dropdown
                    styles={{ dropdown: { width: 200 } }}
                    options={procurementOptions}
                    placeholder={store.i18n.t("schema.vehicle.procurementType")}
                    selectedKeys={this.filteredProcurementTypes}
                    onChange={(_e, item?: IDropdownOption) => {
                        if (!item) {
                            return;
                        }
                        this.setProcurementTypeFilter(item.key as ProcurementType);
                    }}
                    multiSelect
                />
                <ActionButton
                    iconProps={{ iconName: "clearFilter" }}
                    allowDisabledFocus
                    disabled={!this.isFiltered}
                    checked={true}
                    onClick={this.clearFilter}
                ></ActionButton>
            </>
        );
    }
}
